import { ImageIcon, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { uploadApi, uploadToS3, assetsApi } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useActiveFacilityId } from '@/hooks/useActiveFacilityId';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

interface UploadProgress {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  key?: string;
}

export default function LogosUploaderPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const selectedFacility = useActiveFacilityId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(true);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return { valid: false, error: 'Must be PNG, JPEG, GIF, WebP, or SVG' };
    }
    if (file.size > MAX_SIZE_BYTES) {
      return { valid: false, error: 'File must not exceed 5 MB' };
    }
    return { valid: true };
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const validFiles: File[] = [];
    const errors: string[] = [];
    for (const file of Array.from(files)) {
      const v = validateFile(file);
      if (v.valid) validFiles.push(file);
      else errors.push(`${file.name}: ${v.error}`);
    }
    if (errors.length > 0) alert(`Some files were rejected:\n${errors.join('\n')}`);
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  useEffect(() => { loadAssets(); }, [selectedFacility]);

  const loadAssets = async () => {
    try {
      setLoadingAssets(true);
      const params: any = { limit: 100, assetType: 'logo' };
      if (user?.role === 'admin' && selectedFacility) params.facilityId = selectedFacility;
      const response = await assetsApi.list(params);
      setAssets([...response.assets].sort((a, b) => a.originalName.localeCompare(b.originalName)));
    } catch (err) {
      console.error('Failed to load logo assets:', err);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    if (user?.role === 'admin' && !selectedFacility) {
      alert('Please select a facility before uploading.');
      return;
    }

    setIsUploading(true);
    const progress: UploadProgress[] = selectedFiles.map(file => ({
      file, progress: 0, status: 'pending' as const,
    }));
    setUploadProgress(progress);

    const successfulUploads: Array<{
      key: string; filename: string; originalName: string;
      contentType: string; size: number;
      width?: number; height?: number;
      assetType: 'logo';
    }> = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      if (!file) continue;
      try {
        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'uploading' } : p));

        // Get dimensions for raster images (skip for SVG)
        let width: number | undefined;
        let height: number | undefined;
        if (file.type !== 'image/svg+xml') {
          const dims = await new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.width, height: img.height });
            img.src = URL.createObjectURL(file);
          });
          width = dims.width;
          height = dims.height;
        }

        const presignParams: any = {
          filename: file.name,
          contentType: file.type,
          size: file.size,
          assetType: 'logo',
        };
        if (user?.role === 'admin' && selectedFacility) presignParams.facilityId = selectedFacility;

        const presignResponse = await uploadApi.presign(presignParams);

        await uploadToS3(
          { url: presignResponse.uploadUrl, fields: presignResponse.fields },
          file,
          (pct) => setUploadProgress(prev => prev.map((p, idx) =>
            idx === i ? { ...p, progress: pct } : p))
        );

        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'success', key: presignResponse.key } : p));

        successfulUploads.push({
          key: presignResponse.key,
          filename: file.name,
          originalName: file.name,
          contentType: file.type,
          size: file.size,
          ...(width !== undefined ? { width } : {}),
          ...(height !== undefined ? { height } : {}),
          assetType: 'logo',
        });
      } catch (err) {
        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' } : p));
      }
    }

    if (successfulUploads.length > 0) {
      try {
        const notifyParams: any = { uploads: successfulUploads };
        if (user?.role === 'admin' && selectedFacility) notifyParams.facilityId = selectedFacility;
        const notifyResult = await uploadApi.notify(notifyParams);
        if (notifyResult.assets.length < successfulUploads.length) {
          const skipped = successfulUploads.length - notifyResult.assets.length;
          alert(`${notifyResult.assets.length} logo(s) saved. ${skipped} could not be registered — check the server logs.`);
        }
      } catch (err: any) {
        alert(`Upload completed but failed to register with server: ${err?.message ?? 'Unknown error'}`);
      } finally {
        await loadAssets();
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        queryClient.invalidateQueries({ queryKey: ['admin-assets'] });
        queryClient.invalidateQueries({ queryKey: ['facility-assets'] });
        setSelectedFiles([]);
        setTimeout(() => setUploadProgress([]), 3000);
      }
    }

    setIsUploading(false);
  };

  const deleteAsset = async (assetId: number) => {
    if (!confirm('Delete this logo asset?')) return;
    try {
      await assetsApi.delete(assetId);
      await loadAssets();
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['admin-assets'] });
      queryClient.invalidateQueries({ queryKey: ['facility-assets'] });
    } catch (err) {
      console.error('Failed to delete asset:', err);
      alert('Failed to delete asset. Please try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Logos &amp; Banners</h1>
        <p className="text-muted-foreground">
          Upload overlay logos and banner images used by Image widgets — PNG, JPEG, GIF, WebP, or SVG, up to 5 MB, any dimensions
        </p>
      </div>

      {user?.role === 'admin' && !selectedFacility && (
        <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
          Select a facility in the sidebar before uploading logos.
        </div>
      )}

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Upload Area */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Logo / Banner</CardTitle>
            <CardDescription>PNG · JPEG · GIF · WebP · SVG &nbsp;|&nbsp; Max 5 MB &nbsp;|&nbsp; Any dimensions</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); handleFileSelect(e.dataTransfer.files); }}
            >
              <ImageIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <p className="mb-2 text-lg font-medium">Drop logo files here</p>
              <p className="mb-4 text-sm text-muted-foreground">PNG, JPEG, GIF, WebP, or SVG · Max 5 MB · Any size</p>
              <Button onClick={() => fileInputRef.current?.click()}>Browse Files</Button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
                onChange={e => handleFileSelect(e.target.files)}
                className="hidden"
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-3 font-medium">Selected Files ({selectedFiles.length})</h3>
                <div className="space-y-2">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                      <div className="flex items-center gap-3">
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={handleUpload}
                    disabled={isUploading || (user?.role === 'admin' && !selectedFacility)}
                  >
                    {isUploading
                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</>
                      : <>Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}</>}
                  </Button>
                  <Button variant="outline" onClick={() => setSelectedFiles([])} disabled={isUploading}>
                    Clear All
                  </Button>
                </div>
              </div>
            )}

            {uploadProgress.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-3 font-medium">Upload Progress</h3>
                <div className="space-y-3">
                  {uploadProgress.map((p, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {p.status === 'success'   && <CheckCircle className="h-4 w-4 text-green-500" />}
                          {p.status === 'error'     && <AlertCircle className="h-4 w-4 text-red-500" />}
                          {p.status === 'uploading' && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                          {p.status === 'pending'   && <ImageIcon className="h-4 w-4 text-muted-foreground" />}
                          <span className="text-sm font-medium">{p.file.name}</span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {p.status === 'success' && 'Complete'}
                          {p.status === 'error'   && 'Failed'}
                          {p.status === 'uploading' && `${p.progress}%`}
                          {p.status === 'pending' && 'Waiting'}
                        </span>
                      </div>
                      {p.status === 'uploading' && (
                        <div className="h-2 w-full rounded-full bg-muted">
                          <div className="h-2 rounded-full bg-primary transition-all duration-300" style={{ width: `${p.progress}%` }} />
                        </div>
                      )}
                      {p.error && <p className="text-sm text-red-500">{p.error}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logo Library */}
        <Card>
          <CardHeader>
            <CardTitle>
              Logo Library
              {user?.role === 'admin' && selectedFacility && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  for {selectedFacility}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {user?.role === 'admin' && !selectedFacility
                ? 'Select a facility to view logos'
                : 'Logos and banners available for Image widgets'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAssets ? (
              <div className="py-8 text-center">
                <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Loading logos...</p>
              </div>
            ) : assets.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-muted-foreground">No logos uploaded yet</p>
                <p className="mt-2 text-sm text-muted-foreground">Upload a logo to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {assets.map(asset => (
                  <div key={asset.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded bg-muted">
                        <img
                          src={asset.signedUrl ?? asset.s3Url}
                          alt={asset.originalName}
                          className="h-full w-full object-contain"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{asset.originalName}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(asset.fileSize)}
                          {asset.width && asset.height ? ` · ${asset.width}×${asset.height}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(asset.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAsset(asset.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
